import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import {
  addBillingRecipientAsync,
  calculateBillingDraftAsync,
  dispatchBillingSettlementAsync,
  createBillingContractAsync,
  createBillingVersionAsync,
  getBillingChecksAsync,
  getBillingContractsAsync,
  getBillingDispatchesAsync,
  getBillingCorrectionsAsync,
  getBillingContractPatientsAsync,
  createBillingCorrectionAsync,
  deleteBillingCorrectionAsync,
  getBillingReviewLinksAsync,
  getBillingDocumentsAsync,
  getBillingRecipientsAsync,
  getBillingSettlementAsync,
  getBillingSettlementsAsync,
  getBillingVersionsAsync,
  importBillingSettlementAsync,
  removeBillingRecipientAsync,
  transitionBillingSettlementAsync,
  updateBillingAutoDraftAsync,
  updateBillingIssueOnValidationAsync,
  updateBillingVersionPriceAsync,
  uploadBillingEvidenceAsync,
} from './asyncActions';

/** Operaciones que cambian algo: mientras corren, la pantalla deshabilita sus botones. */
const writes = [
  calculateBillingDraftAsync,
  importBillingSettlementAsync,
  transitionBillingSettlementAsync,
  dispatchBillingSettlementAsync,
  uploadBillingEvidenceAsync,
  addBillingRecipientAsync,
  removeBillingRecipientAsync,
  updateBillingAutoDraftAsync,
  updateBillingIssueOnValidationAsync,
  updateBillingVersionPriceAsync,
  createBillingVersionAsync,
  createBillingCorrectionAsync,
  deleteBillingCorrectionAsync,
  createBillingContractAsync,
] as const;

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    selectBillingContract: (state, { payload }: PayloadAction<string | null>) => {
      state.selectedContractId = payload;
    },
    resetBillingDetail: (state) => {
      state.detail = initialState.detail;
      state.dispatches = initialState.dispatches;
      state.documents = initialState.documents;
      state.reviewLinks = initialState.reviewLinks;
      state.checks = initialState.checks;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBillingContractsAsync.pending, (state) => {
        state.contracts.status = 'loading';
      })
      .addCase(getBillingContractsAsync.fulfilled, (state, { payload }) => {
        state.contracts.status = 'idle';
        state.contracts.data = payload ?? [];
        const ids = state.contracts.data.map((c) => c.id);
        if (!state.selectedContractId || !ids.includes(state.selectedContractId)) {
          state.selectedContractId = state.contracts.data.find((c) => !c.archived)?.id ?? ids[0] ?? null;
        }
      })
      .addCase(getBillingContractsAsync.rejected, (state) => {
        state.contracts.status = 'failed';
      })

      .addCase(getBillingSettlementsAsync.pending, (state) => {
        state.settlements.status = 'loading';
      })
      .addCase(getBillingSettlementsAsync.fulfilled, (state, { payload }) => {
        state.settlements.status = 'idle';
        state.settlements.data = payload ?? [];
      })
      .addCase(getBillingSettlementsAsync.rejected, (state) => {
        state.settlements.status = 'failed';
      })

      .addCase(getBillingSettlementAsync.pending, (state) => {
        state.detail.status = 'loading';
      })
      .addCase(getBillingSettlementAsync.fulfilled, (state, { payload }) => {
        state.detail.status = 'idle';
        state.detail.data = payload;
      })
      .addCase(getBillingSettlementAsync.rejected, (state) => {
        state.detail.status = 'failed';
      })

      .addCase(getBillingDispatchesAsync.pending, (state) => {
        state.dispatches.status = 'loading';
      })
      .addCase(getBillingDispatchesAsync.fulfilled, (state, { payload }) => {
        state.dispatches.status = 'idle';
        state.dispatches.data = payload ?? [];
      })
      .addCase(getBillingCorrectionsAsync.pending, (state) => {
        state.corrections.status = 'loading';
      })
      .addCase(getBillingCorrectionsAsync.fulfilled, (state, { payload }) => {
        state.corrections.status = 'idle';
        state.corrections.data = payload ?? [];
      })
      .addCase(getBillingCorrectionsAsync.rejected, (state) => {
        state.corrections.status = 'failed';
      })
      .addCase(getBillingContractPatientsAsync.pending, (state) => {
        state.contractPatients.status = 'loading';
      })
      .addCase(getBillingContractPatientsAsync.fulfilled, (state, { payload }) => {
        state.contractPatients.status = 'idle';
        state.contractPatients.data = payload ?? [];
      })
      .addCase(getBillingContractPatientsAsync.rejected, (state) => {
        state.contractPatients.status = 'failed';
      })
      .addCase(createBillingCorrectionAsync.fulfilled, (state, { payload }) => {
        state.corrections.data = [payload, ...state.corrections.data];
      })
      .addCase(deleteBillingCorrectionAsync.fulfilled, (state, { meta }) => {
        state.corrections.data = state.corrections.data.filter((c) => c.id !== meta.arg.correctionId);
      })
      .addCase(getBillingReviewLinksAsync.pending, (state) => {
        state.reviewLinks.status = 'loading';
      })
      .addCase(getBillingReviewLinksAsync.fulfilled, (state, { payload }) => {
        state.reviewLinks.status = 'idle';
        state.reviewLinks.data = payload ?? [];
      })
      .addCase(getBillingReviewLinksAsync.rejected, (state) => {
        state.reviewLinks.status = 'failed';
      })
      .addCase(getBillingDispatchesAsync.rejected, (state) => {
        state.dispatches.status = 'failed';
      })

      .addCase(getBillingDocumentsAsync.pending, (state) => {
        state.documents.status = 'loading';
      })
      .addCase(getBillingDocumentsAsync.fulfilled, (state, { payload }) => {
        state.documents.status = 'idle';
        state.documents.data = payload ?? [];
      })
      .addCase(getBillingDocumentsAsync.rejected, (state) => {
        state.documents.status = 'failed';
      })

      .addCase(getBillingRecipientsAsync.pending, (state) => {
        state.recipients.status = 'loading';
      })
      .addCase(getBillingRecipientsAsync.fulfilled, (state, { payload }) => {
        state.recipients.status = 'idle';
        state.recipients.data = payload ?? [];
      })
      .addCase(getBillingRecipientsAsync.rejected, (state) => {
        state.recipients.status = 'failed';
      })

      .addCase(getBillingChecksAsync.pending, (state) => {
        state.checks.status = 'loading';
      })
      .addCase(getBillingChecksAsync.fulfilled, (state, { payload }) => {
        state.checks.status = 'idle';
        state.checks.data = payload ?? [];
      })
      .addCase(getBillingChecksAsync.rejected, (state) => {
        state.checks.status = 'failed';
      })

      // Contrato nuevo: entra en la lista y queda elegido.
      .addCase(createBillingContractAsync.fulfilled, (state, { payload }) => {
        if (payload) {
          state.contracts.data = [...state.contracts.data.filter((c) => c.id !== payload.id), payload].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          state.selectedContractId = payload.id;
        }
      })

      .addCase(getBillingVersionsAsync.pending, (state) => {
        state.versions.status = 'loading';
      })
      .addCase(getBillingVersionsAsync.fulfilled, (state, { payload }) => {
        state.versions.status = 'idle';
        state.versions.data = payload ?? [];
      })
      .addCase(getBillingVersionsAsync.rejected, (state) => {
        state.versions.status = 'failed';
      })
      // Los cambios de tarifa devuelven la lista de versiones resultante.
      .addCase(updateBillingVersionPriceAsync.fulfilled, (state, { payload }) => {
        state.versions.data = payload ?? [];
      })
      .addCase(createBillingVersionAsync.fulfilled, (state, { payload }) => {
        state.versions.data = payload ?? [];
      })

      .addCase(updateBillingAutoDraftAsync.fulfilled, (state, { payload, meta }) => {
        const contract = state.contracts.data.find((c) => c.id === meta.arg.contractId);
        if (contract) contract.autoDraftDay = payload ?? null;
      })
      .addCase(updateBillingIssueOnValidationAsync.fulfilled, (state, { payload, meta }) => {
        const contract = state.contracts.data.find((c) => c.id === meta.arg.contractId);
        if (contract) contract.issueOnClientValidation = !!payload;
      })

      // Las transiciones devuelven la liquidación con sus líneas: se refresca el detalle sin otra petición.
      .addCase(transitionBillingSettlementAsync.fulfilled, (state, { payload }) => {
        state.detail.data = payload;
      })
      .addCase(calculateBillingDraftAsync.fulfilled, (state, { payload }) => {
        if (state.detail.data?.id === payload.id) {
          state.detail.data = payload;
        }
      })

      .addMatcher(isAnyOf(...writes.map((t) => t.pending)), (state) => {
        state.busy = true;
      })
      .addMatcher(isAnyOf(...writes.map((t) => t.fulfilled), ...writes.map((t) => t.rejected)), (state) => {
        state.busy = false;
      });
  },
});

export const { selectBillingContract, resetBillingDetail } = billingSlice.actions;

export default billingSlice.reducer;
